/**
 * LiveTradingPhase Component
 *
 * Live trading cockpit dashboard — fixed-viewport workspace where the trader
 * has immediate access to all feature areas without scrolling.
 *
 * Renders a configurable SplitPane-based pane grid (via LayoutRenderer)
 * with empty-state slots, and a configurable banner bar (BannerBar) with
 * top/bottom positioning. Feature area drawers are preserved for T10.
 * Actual feature implementations are provided by WU2-WU6.
 *
 * Manages transient expanded-pane state (`expandedSlotId`) — not persisted
 * to layout config. Clears on preset change to prevent stale references.
 */

// 1. External dependencies
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';

// 2. Internal absolute imports
import {
  Drawer, DrawerContent, DrawerHeader, DrawerBody,
  Button, Divider, EmptyState,
} from '@enfomentis/ui';
import { FEATURE_AREAS } from '@/constants/featureArea';
import { LAYOUT_PRESETS } from '@/constants/layout';
import { useAlarmEngine } from '@/hooks/useAlarmEngine';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { db } from '@/lib/db';
import { confluenceService } from '@/services/ConfluenceService';
import { criteriaService } from '@/services/CriteriaService';
import { entryModelService } from '@/services/EntryModelService';
import { liveTradingSettingsService } from '@/services/LiveTradingSettingsService';
import { playbookService } from '@/services/PlaybookService';
import { sessionDraftService } from '@/services/SessionDraftService';
import { strategyService } from '@/services/StrategyService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';

// 3. Type imports
import type { AlarmRule } from '@/types/shared/AlarmRule';
import type { Confluence } from '@/types/schema/Confluence';
import type { EntryModel } from '@/types/schema/EntryModel';
import type { Playbook } from '@/types/schema/Playbook';
import type { PlaybookCriteriaItem } from '@/types/schema/PlaybookCriteriaItem';
import type { Strategy } from '@/types/schema/Strategy';
import type { ClockWidgetConfig, FeatureAreaConfig, FeatureAreaId, FlushHandler, LayoutConfig, LayoutPresetId, SlotId, SplitPointId } from '@/types/shared';
import type { ResolvedBannerConfig } from '@/hooks/useLayoutConfig';
import type { TradeDraft } from '@/types/transient/TradeDraft';

// 4. Relative imports
import { BannerBar } from './BannerBar';
import { LayoutRenderer } from './LayoutRenderer';
import { TradesPane } from './TradesPane';
import type { TradesPaneActions } from './TradesPane';

// ===== HELPERS =====

/** Look up a feature area config by ID */
function getFeatureArea(id: FeatureAreaId): FeatureAreaConfig | undefined {
  return FEATURE_AREAS.find((area) => area.id === id);
}

// ===== MAIN COMPONENT =====

export interface LiveTradingPhaseProps {
  /** Register this phase's flush handler with TradingHub for header-level invocation */
  registerFlushHandler: (handler: FlushHandler) => void;
  /** Layout configuration from useLayoutConfig — null while loading */
  layoutConfig: LayoutConfig | null;
  /** Resolved preset ID (falls back to 4-pane-grid if stored preset unsupported) */
  effectivePresetId: LayoutPresetId;
  /** Resolved banner config with runtime defaults applied */
  resolvedBannerConfig: ResolvedBannerConfig;
  /** Report pane weight changes for debounced auto-save */
  onWeightChange: (splitPointId: SplitPointId, weight: number) => void;
  /** Report slot assignment changes for debounced auto-save */
  onSlotAssignmentChange: (slotAssignments: Partial<Record<SlotId, FeatureAreaId>>) => void;
  /** Flush pending pane weight saves (called before phase transition) */
  flushPendingWeights: () => Promise<void>;
  /** Session draft ID — used to load and persist trades */
  sessionDraftId?: string;
  /**
   * Session-default trading account ID — forwarded to TradesPane → TradeDrawer.
   * The ExecutionEventModal lets the user override this at promotion time.
   * Undefined until pre-trading account selection is wired (future work).
   */
  accountId?: string;
  /** Update clock widget override config (immediate persist) */
  onUpdateClockConfig?: (config: ClockWidgetConfig | undefined) => void;
  /**
   * T05 (WU2E): Called whenever the Trades accordion menu content changes.
   * Fires once after TradesPane loads its settings, then again if settings are saved.
   * Caller (TradingHub) stores the content and passes it to FeaturesDropdown.
   */
  onTradesMenuChange?: (content: React.ReactNode) => void;
}

/**
 * Live Trading Phase — Cockpit Dashboard
 *
 * Fixed-viewport workspace with:
 * - Configurable banner bar (BannerBar) — top or bottom position
 * - Configurable SplitPane-based pane grid (LayoutRenderer)
 * - Empty-state slots in each pane position
 * - Drawer-based feature content overlays (preserved for T10)
 */
export const LiveTradingPhase: React.FC<LiveTradingPhaseProps> = ({
  registerFlushHandler,
  layoutConfig,
  effectivePresetId,
  resolvedBannerConfig,
  onWeightChange,
  onSlotAssignmentChange,
  flushPendingWeights,
  sessionDraftId,
  accountId,
  onUpdateClockConfig,
  onTradesMenuChange,
}) => {
  const isDesktop = useIsDesktop();

  // Active feature area — null means no drawer open (preserved for T10)
  const [activeFeatureArea, setActiveFeatureArea] = useState<FeatureAreaId | null>(null);
  // Expanded/focus pane — undefined means no pane expanded (transient, not persisted)
  const [expandedSlotId, setExpandedSlotId] = useState<SlotId | undefined>(undefined);

  // ---------------------------------------------------------------------------
  // Alarm engine — loaded from LiveTradingSettings, reactive to config changes
  // ---------------------------------------------------------------------------

  const [alarmRules, setAlarmRules] = useState<AlarmRule[]>([]);
  const [alarmAutoDismissMs, setAlarmAutoDismissMs] = useState(15000);

  /** Monotonic counter to discard stale async responses */
  const alarmFetchIdRef = useRef(0);

  useEffect(() => {
    let mounted = true;

    /** Load alarm config from service, guarded against stale responses */
    const loadAlarmConfig = (): void => {
      const fetchId = ++alarmFetchIdRef.current;
      liveTradingSettingsService.getOrCreateDefaultSettings().then((settings) => {
        if (!mounted || fetchId !== alarmFetchIdRef.current) return;
        setAlarmRules(settings.alarmRules ?? []);
        setAlarmAutoDismissMs(settings.alarmAutoDismissMs ?? 15000);
      }).catch(() => { /* silent — engine runs with defaults */ });
    };

    // Initial load
    loadAlarmConfig();

    // Subscribe to live_trading_settings changes (same-tab + cross-tab)
    const unsubscribe = syncCoordinatorService.subscribe(
      'live_trading_settings',
      () => { loadAlarmConfig(); },
    );

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  // Mount alarm engine — evaluates rules every second, fires toasts + sounds
  useAlarmEngine({ alarmRules, alarmAutoDismissMs });

  // ---------------------------------------------------------------------------
  // Reference data — strategies, playbooks, criteria, confluences, entry models
  // Loaded once on mount; passed to TradesPane → TradeDrawer + QuickTradeSettingsModal
  // ---------------------------------------------------------------------------

  const [activeStrategies, setActiveStrategies] = useState<Strategy[]>([]);
  const [activePlaybooks, setActivePlaybooks] = useState<Playbook[]>([]);
  const [activeCriteria, setActiveCriteria] = useState<PlaybookCriteriaItem[]>([]);
  const [activeConfluences, setActiveConfluences] = useState<Confluence[]>([]);
  const [activeEntryModels, setActiveEntryModels] = useState<EntryModel[]>([]);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      strategyService.getActiveStrategies(),
      playbookService.getActivePlaybooks(),
      criteriaService.getActiveCriteria(),
      confluenceService.getActiveConfluences(),
      entryModelService.getActiveEntryModels(),
    ]).then(([strategies, playbooks, criteria, confluences, entryModels]) => {
      if (!mounted) return;
      setActiveStrategies(strategies);
      setActivePlaybooks(playbooks);
      setActiveCriteria(criteria);
      setActiveConfluences(confluences);
      setActiveEntryModels(entryModels);
    }).catch(() => { /* silent — empty arrays shown */ });
    return () => { mounted = false; };
  }, []);

  // ---------------------------------------------------------------------------
  // Trades state — loaded from session draft on mount, updated on each commit
  // ---------------------------------------------------------------------------

  const [trades, setTrades] = useState<TradeDraft[]>([]);
  const [sessionAccountId, setSessionAccountId] = useState<string | undefined>(undefined);

  /** Tracks the latest in-flight updateTrades() promise for flush-handler await */
  const pendingTradesWriteRef = useRef<Promise<unknown> | null>(null);

  useEffect(() => {
    if (!sessionDraftId) return;
    let mounted = true;
    db.session_drafts.get(sessionDraftId).then((draft) => {
      if (!mounted || !draft) return;
      setTrades(draft.trades ?? []);
      setSessionAccountId(draft.defaultAccountId);
    }).catch(() => { /* silent — empty state shown */ });
    return () => { mounted = false; };
  }, [sessionDraftId]);

  const handleUpdateTrades = useCallback(async (updatedTrades: TradeDraft[]): Promise<void> => {
    if (!sessionDraftId) return;
    setTrades(updatedTrades);
    const p = sessionDraftService.updateTrades(sessionDraftId, updatedTrades);
    pendingTradesWriteRef.current = p;
    await p;
  }, [sessionDraftId]);

  // ---------------------------------------------------------------------------
  // T05 (WU2E): Trades menu content for FeaturesDropdown
  // ---------------------------------------------------------------------------

  const [tradesActions, setTradesActions] = useState<TradesPaneActions | null>(null);

  const tradesMenuContent = useMemo((): React.ReactNode => {
    if (!tradesActions) return null;
    const { newTrade, presetOpen, openSettings, presets, showQuickTrades: show } = tradesActions;
    return (
      <div className="flex flex-col gap-1">
        <Button size="sm" variant="flat" color="primary" className="w-full" onPress={newTrade}>
          New Trade
        </Button>
        {show && presets.length > 0 && (
          <>
            <Divider className="my-1" />
            {presets.map(preset => (
              <Button
                key={preset.id}
                size="sm"
                variant="light"
                className="w-full justify-start"
                onPress={() => presetOpen(preset)}
              >
                {preset.name}
              </Button>
            ))}
          </>
        )}
        <Divider className="my-1" />
        <Button size="sm" variant="light" className="w-full" onPress={() => { openSettings(); }}>
          Manage Quick Trade Settings
        </Button>
      </div>
    );
  }, [tradesActions]);

  /** Holds latest onTradesMenuChange without including it in effect deps */
  const onTradesMenuChangeRef = useRef(onTradesMenuChange);
  onTradesMenuChangeRef.current = onTradesMenuChange;

  useEffect(() => {
    onTradesMenuChangeRef.current?.(tradesMenuContent);
  }, [tradesMenuContent]);

  // ---------------------------------------------------------------------------
  // Feature content map — body content + header actions per feature area
  // ---------------------------------------------------------------------------

  const resolvedAccountId = sessionAccountId ?? accountId;

  const featureContentMap = useMemo(() => ({
    trades: {
      content: (
        <TradesPane
          trades={trades}
          onUpdateTrades={handleUpdateTrades}
          {...(sessionDraftId !== undefined ? { sessionDraftId } : {})}
          {...(resolvedAccountId !== undefined ? { defaultAccountId: resolvedAccountId } : {})}
          onActionsChange={setTradesActions}
          activeStrategies={activeStrategies}
          activePlaybooks={activePlaybooks}
          activeCriteria={activeCriteria}
          activeConfluences={activeConfluences}
          activeEntryModels={activeEntryModels}
        />
      ),
    },
  }), [trades, handleUpdateTrades, sessionDraftId, resolvedAccountId, activeStrategies, activePlaybooks, activeCriteria, activeConfluences, activeEntryModels]);

  // Close drawer (preserved for T10)
  const handleDrawerClose = useCallback((): void => {
    setActiveFeatureArea(null);
  }, []);

  // Toggle expand/focus mode on a pane — same slot collapses, different slot replaces
  const handleExpandToggle = useCallback((slotId: SlotId): void => {
    setExpandedSlotId((prev) => (prev === slotId ? undefined : slotId));
  }, []);

  // Clear expanded state when layout preset changes (slot may not exist in new preset)
  useEffect(() => {
    setExpandedSlotId(undefined);
  }, [effectivePresetId]);

  /**
   * Flush pending autosave data — async contract for header-level invocation.
   * Flushes pending pane weight saves. WU2-WU6 will add additional
   * awaited service calls here as feature areas gain autosave capabilities.
   */
  const flushPendingData = useCallback(async (): Promise<void> => {
    await pendingTradesWriteRef.current;
    await flushPendingWeights();
  }, [flushPendingWeights]);

  // Register this phase's flush handler with TradingHub
  useEffect(() => {
    registerFlushHandler(flushPendingData);
  }, [registerFlushHandler, flushPendingData]);

  // Look up active feature config for drawer content (preserved for T10)
  const activeConfig = activeFeatureArea !== null ? getFeatureArea(activeFeatureArea) : null;

  const bannerBar = resolvedBannerConfig.visible
    ? <BannerBar resolvedBannerConfig={resolvedBannerConfig} {...(onUpdateClockConfig ? { onUpdateClockConfig } : {})} />
    : null;

  return (
    <div className="h-full flex flex-col">
      {/* Banner Bar — top position */}
      {resolvedBannerConfig.position === 'top' && bannerBar}

      {/* Layout Renderer — configurable pane grid */}
      <div className="flex-1 min-h-0">
        {layoutConfig ? (
          <LayoutRenderer
            key={effectivePresetId}
            presetConfig={LAYOUT_PRESETS[effectivePresetId]}
            paneWeights={layoutConfig.workingLayout.paneWeights}
            slotAssignments={layoutConfig.workingLayout.slotAssignments}
            onWeightChange={onWeightChange}
            onSlotAssignmentChange={onSlotAssignmentChange}
            featureContentMap={featureContentMap}
            {...(expandedSlotId !== undefined ? { expandedSlotId } : {})}
            onExpandToggle={handleExpandToggle}
          />
        ) : (
          <div className="h-full flex items-center justify-center">
            <p className="text-text-secondary">Loading layout…</p>
          </div>
        )}
      </div>

      {/* Banner Bar — bottom position */}
      {resolvedBannerConfig.position === 'bottom' && bannerBar}

      {/* Feature Area Drawer — preserved for T10 (no trigger in T09) */}
      <Drawer
        isOpen={activeFeatureArea !== null}
        onOpenChange={(open) => { if (!open) handleDrawerClose(); }}
        placement={isDesktop ? 'right' : 'bottom'}
        backdrop={isDesktop ? 'transparent' : 'opaque'}
        size="lg"
      >
        <DrawerContent>
          {activeConfig && (
            <>
              <DrawerHeader>
                <div className="flex items-center gap-3">
                  <activeConfig.Icon size={20} className={activeConfig.iconColorClass} />
                  <h2 className="text-lg font-semibold text-text-primary">{activeConfig.label}</h2>
                </div>
              </DrawerHeader>
              <DrawerBody>
                <EmptyState
                  title={`${activeConfig.label} — Coming Soon`}
                  description={activeConfig.emptyHint}
                  icon={<activeConfig.Icon size={32} className={activeConfig.iconColorClass} />}
                />
              </DrawerBody>
            </>
          )}
        </DrawerContent>
      </Drawer>
    </div>
  );
};
